/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import Footer from "../../components/main/Footer"
import Basket from "../../components/organisms/Basket"
import { Header } from "../../components/organisms/Header"
import { Topbar } from "../../components/organisms/Topbar"
import { FINANCERS, TENANTS, TENANTS_BY_PAYMENT_PROVIDERS } from "../../constants"
import { initPdiComponent } from "../../core/service/BusinessLogicService"
import { isPdiRetailTenant, loc } from "../../core/utils"
import FailPage from "../FailPage"
import { AdyenCBForm, AxeptaCBForm, PaylineCBForm } from "./components"
import Iframe from "./components/3DSecureIframe/3DSecureIframe"
import { processAdyenPayment } from "./service/Adyen/AdyenPaymentService"
import { handleAxeptaResponse, processAxeptaPayment } from "./service/Axepta/axepta_checkout/AxeptaPaymentService"
import validator from "./validate"
import "@adyen/adyen-web/dist/adyen.css"
import { setIn } from "final-form"
import React, { useEffect, useState } from "react"
import { Alert, Button, Col, Container, Row } from "react-bootstrap"
import Modal from "react-bootstrap/Modal"
import * as FinalForm from "react-final-form"
import { useHistory } from "react-router-dom"
import ClipLoader from "react-spinners/ClipLoader"

export default function (props) {
  const { tunnelData, onSetStep, history } = props
  const { tenant, shopUrl } = tunnelData

  const historyNavigate = useHistory()

  const [isButtonLocked, setIsButtonLock] = useState(true)
  const [hasAlreadyFailedCb, setHasAlreadyFailedCb] = useState(false)
  const [axeptaExecuteResponse, setAxeptaExecuteResponse] = useState(null)
  const [showAdyenModal, setShowAdyenModal] = useState(false)

  useEffect(() => {
    initPdiComponent("CBPage")

    if (tunnelData.pspName === "axepta") {
      //add axepta listener to handle axepta 3DS response
      window.addEventListener(
        "message",
        (event) => handleAxeptaResponse(event, props, setHasAlreadyFailedCb, setAxeptaExecuteResponse),
        false
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (axeptaExecuteResponse?.axeptaCardType === "NO_3DS") {
      props.onSetStep()
    }
    setIsButtonLock(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axeptaExecuteResponse])

  const validate = async (values) => {
    let errors = []
    if (tunnelData.pspName !== "adyen") {
      errors = await validator(values).catch((invalideFields) => {
        return invalideFields.inner
          .filter((field) => field.errors.length > 0)
          .reduce((errors, errorField) => setIn(errors, errorField.path, errorField.message), {})
      })
    }
    return errors
  }

  const certifySignature =
    tunnelData.tenant === "micromania"
      ? `${tunnelData?.data?.person?.firstName} ${tunnelData?.data?.person?.name}`
      : `${tunnelData?.data?.order?.customer?.person?.firstName} ${tunnelData?.data?.order?.customer?.person?.name}`

  const checkbox = document.getElementById("userIsRealCardOwner")
  const onSubmit = async (values) => {
    setIsButtonLock(true)
    if (tunnelData.pspName === "payline") {
      if (tunnelData.pspIntegrationWorkflow === "payline_3ds") {
        const btnValidPayline = document.getElementsByClassName("pl-pay-btn")[0]
        btnValidPayline.click()
      }
    } else if (tunnelData.pspName === "axepta") {
      if (tunnelData.pspIntegrationWorkflow === "axepta_checkout") {
        processAxeptaPayment(tunnelData, values, setAxeptaExecuteResponse, setHasAlreadyFailedCb, setIsButtonLock)
      }
    } else if (tunnelData.pspName === "adyen") {
      if (tunnelData.pspIntegrationWorkflow === "adyen_ideal") {
        processAdyenPayment(tunnelData, onSetStep, setHasAlreadyFailedCb, setIsButtonLock, setShowAdyenModal)
      }
    } else {
      console.error(`No psp integration workflow found for payment provider: ${tunnelData.pspName}`)
    }
  }

  const formatPrice = (price) => {
    return tunnelData.tenant === TENANTS.SAMSUNG_NL ? `€${price}` : `${price} €`
  }

  const backToIdentificationPage = () => {
    if (isPdiRetailTenant(tenant)) {
      tunnelData.step = "stepIdentification"
      historyNavigate.push("/digital-journey/identification")
    }
  }

  const getPaymentProviderCBForm = () => {
    if (TENANTS_BY_PAYMENT_PROVIDERS.PAYLINE.includes(tunnelData.tenant)) {
      return <PaylineCBForm certifySignature={certifySignature} />
    }
    if (TENANTS_BY_PAYMENT_PROVIDERS.AXEPTA.includes(tunnelData.tenant)) {
      return <AxeptaCBForm certifySignature={certifySignature} />
    }
    if (TENANTS_BY_PAYMENT_PROVIDERS.ADYEN.includes(tunnelData.tenant)) {
      return <AdyenCBForm tunnelData={tunnelData} />
    }
    return <FailPage message="Payment provider not available. Please try again later." />
  }

  return (
    <Container fluid className="white-background">
      <Row noGutters>
        {/* ——————————————————————————————————————————————— */}
        {/* ———————————————————— MAIN ————————————————————— */}
        {/* ——————————————————————————————————————————————— */}
        <Col className="main-block">
          <Row>
            <Topbar tunnelData={tunnelData} history={history} />
            <Header tunnelData={tunnelData} />

            {/* CONTENT */}
            <Col sm={12}>
              <Container>
                <FinalForm.Form
                  onSubmit={onSubmit}
                  {...(tunnelData.pspName === "payline" ? {} : { validate: validate })}
                >
                  {({ handleSubmit, hasValidationErrors, errors }) => {
                    return (
                      <form onSubmit={handleSubmit}>
                        <div className="solid-brand-block">
                          <Row>
                            <Col className="align-self-center">
                              <h4>{loc`first installment of your rental contract`}</h4>
                            </Col>
                            <Col className="col-auto align-self-center">
                              <p className="text-right">
                                {formatPrice(tunnelData?.data?.order?.rent?.firstCustomerPayment)}
                              </p>
                            </Col>
                          </Row>
                        </div>

                        <div className="hard-shadow-white-block has-wider-padding">
                          {(hasAlreadyFailedCb || tunnelData.paylineError || tunnelData.adyenError) && (
                            <Alert variant="danger">
                              <p>{loc`Your card has been declined. Please check the accuracy of the information entered. You can also contact your bank to verify that it is not blocking payment.`}</p>
                            </Alert>
                          )}
                          {[FINANCERS.PFLOC].includes(tunnelData.data.order.financerType) && (
                            <Row>
                              <Col sm={12}>
                                <Alert variant="info">
                                  <p>
                                    <strong>{loc`This step only constitutes a request for authorization of payment.`}</strong>
                                    {loc` The 1st payment of `}
                                    {tunnelData?.data?.order?.rent?.firstCustomerPayment || 0} €
                                    {loc` by credit card will only be effective after signing your rental contract and downloading your supporting documents.If your rental request is subsequently refused by the Lessor following its study, then this amount will be immediately refunded.`}
                                  </p>
                                </Alert>
                              </Col>
                            </Row>
                          )}

                          {getPaymentProviderCBForm()}

                          {[FINANCERS.PERSONAL_RENTING].includes(tunnelData.data.order.financerType) ? (
                            <Row>
                              <Col sm={12}>
                                <Alert variant="info">
                                  <p>
                                    {loc`the_first_payment_of ${
                                      tunnelData?.data?.order?.rent?.firstCustomerPayment || 0
                                    } will_be_accounted`}
                                  </p>
                                </Alert>
                              </Col>
                            </Row>
                          ) : null}
                        </div>

                        <Row className="footer-fixed">
                          <Col
                            lg={{ span: 3, order: 1 }}
                            md={{ span: 4, order: 1 }}
                            sm={{ span: 12, order: 3 }}
                            xs={{ span: 12, order: 3 }}
                            className="mr-auto"
                          >
                            <Button
                              {...(shopUrl ? { href: shopUrl } : { onClick: backToIdentificationPage })}
                              variant="outline-primary"
                              className="ico-back-small-before cb-page-back-button"
                            >
                              {loc`Back`}
                            </Button>
                          </Col>
                          <Col
                            lg={{ span: 4, order: 2 }}
                            md={{ span: 2, order: 2 }}
                            sm={{ span: 6, order: 2 }}
                            xs={{ span: 12, order: 1 }}
                            className="align-self-center"
                          >
                            <p className="secured-payment ico-secure-small-before">{loc`Secured payment`}</p>
                          </Col>
                          <Col
                            lg={{ span: 4, order: 3 }}
                            md={{ span: 6, order: 3 }}
                            sm={{ span: 6, order: 2 }}
                            xs={{ span: 12, order: 2 }}
                            className="align-item-end"
                          >
                            <Button
                              id="button-validate"
                              variant="success"
                              type="submit"
                              className="btn-solid btn"
                              disabled={tunnelData.pspName === "payline" ? !checkbox?.checked : hasValidationErrors}
                            >
                              {!isButtonLocked ? loc`validate the payment authorization` : <ClipLoader size={35} />}
                            </Button>
                          </Col>
                        </Row>
                      </form>
                    )
                  }}
                </FinalForm.Form>
              </Container>
            </Col>
            {/* END CONTENT */}

            <Col sm={12}>
              <Container>
                <Footer tunnelData={tunnelData} />
              </Container>
            </Col>
          </Row>
        </Col>

        {/* ————————————————————————————————————————————————— */}
        {/* ————————————————————— ASIDE ————————————————————— */}
        {/* ————————————————————————————————————————————————— */}

        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <Basket tunnelData={tunnelData} />
        </Col>
      </Row>

      <div id="acs-evollis-widget" />
      {["V1_3DS", "V2_3DS"].includes(axeptaExecuteResponse?.axeptaCardType) && (
        <Iframe axeptaResponse={axeptaExecuteResponse} />
      )}

      <Modal
        show={showAdyenModal}
        onHide={() => setShowAdyenModal(false)}
        backdrop="static"
        keyboard={false}
        className="modal-info"
      >
        <Modal.Body>
          <div id="dropin-container" />
        </Modal.Body>
        <Modal.Footer>
          <Row className="justify-content-md-center">
            <Col md={6} lg={6}>
              <Button variant="outline-primary" onClick={() => setShowAdyenModal(false)}>
                {loc`Cancel`}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
